import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "@carbon/icons-react";
import { callMongoApi } from "../../services/mongoApiService";

const PAGE_SIZE = 20;

const timeAgo = (iso) => {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (isNaN(diff)) return "";
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} day(s) ago`;
};

const TABS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
];

export default function NotificationsPage() {
  const navigate = useNavigate();
  const sentinelRef = useRef(null);

  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const [readIds, setReadIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("read_notification_ids")) || [];
    } catch {
      return [];
    }
  });

  const isRead = (n) => n.isRead === true || readIds.includes(n.notificationId);

  // Filter items client-side by tab
  const filteredItems = items.filter((n) => {
    if (tab === "unread") return !isRead(n);
    if (tab === "read") return isRead(n);
    return true;
  });

  const fetchNotifications = useCallback(
    async (targetPage = 1, { append = false } = {}) => {
      setLoading(true);
      try {
        const res = await callMongoApi(
          "GET",
          `/device/notifications?page=${targetPage}&limit=${PAGE_SIZE}&isAdmin=true`
        );
        const payload = res?.data || {};
        const list = payload.notifications || [];
        const pagination = payload.pagination || {};
        const stats = payload.stats || {};

        setItems((prev) => (append ? [...prev, ...list] : list));
        setPage(targetPage);
        setTotal(pagination.total ?? list.length);
        setHasMore(
          pagination.totalPages != null
            ? targetPage < pagination.totalPages
            : list.length === PAGE_SIZE
        );
        if (stats.unread != null) setUnreadCount(stats.unread);
      } catch (err) {
        console.error("Failed to load notifications:", err);
        if (!append) setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Initial + tab change reload
  useEffect(() => {
    fetchNotifications(1, { append: false });
  }, [fetchNotifications]);

  // Infinite scroll
  useEffect(() => {
    if (!hasMore || loading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchNotifications(page + 1, { append: true });
        }
      },
      { rootMargin: "120px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, page, fetchNotifications]);

  const markRead = async (ids) => {
    if (!ids || ids.length === 0) return;

    const next = Array.from(new Set([...readIds, ...ids]));
    setReadIds(next);
    localStorage.setItem("read_notification_ids", JSON.stringify(next));
    setUnreadCount((prev) => Math.max(0, prev - ids.length));

    try {
      await callMongoApi("PUT", "/device/notifications/read-all?isAdmin=true", {
        notificationIds: ids,
      });
    } catch (err) {
      console.error("Failed to mark as read on server:", err);
    }
  };

  const handleItemClick = (n) => {
    markRead([n.notificationId]);
    const propertyId = n?.data?.propertyId;
    if (propertyId) {
      navigate(
        `/settings/property-management/view/${encodeURIComponent(propertyId)}`
      );
    }
  };

  const handleMarkAllRead = () => {
    markRead(items.map((n) => n.notificationId));
  };

  return (
    <div style={{ padding: "1.5rem", maxWidth: 900, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            background: "none",
            border: 0,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            padding: 4,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Notifications</h2>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          marginBottom: "1rem",
        }}
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          const count =
            t.key === "unread"
              ? unreadCount
              : t.key === "all"
              ? total
              : undefined;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              style={{
                background: "none",
                border: 0,
                padding: "0.75rem 1rem",
                cursor: "pointer",
                color: active ? "#0f62fe" : "#525252",
                fontWeight: active ? 600 : 400,
                borderBottom: active ? "2px solid #0f62fe" : "2px solid transparent",
                fontSize: 14,
              }}
            >
              {t.label}
              {count != null ? ` (${count})` : ""}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={handleMarkAllRead}
            style={{
              background: "none",
              border: 0,
              color: "#0f62fe",
              cursor: "pointer",
              fontSize: 13,
              padding: "0.5rem 0.75rem",
            }}
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* List */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: 4,
        }}
      >
        {filteredItems.length === 0 && !loading && (
          <div style={{ padding: "2rem", textAlign: "center", color: "#6f6f6f" }}>
            No {tab === "all" ? "" : tab} notifications
          </div>
        )}

        {filteredItems.map((n) => (
          <div
            key={n._id}
            onClick={() => handleItemClick(n)}
            style={{
              padding: "0.9rem 1rem",
              borderBottom: "1px solid #e0e0e0",
              cursor: "pointer",
              background: isRead(n) ? "#fff" : "#edf5ff",
            }}
          >
            <div
              style={{
                fontWeight: isRead(n) ? 400 : 600,
                fontSize: 14,
                marginBottom: 2,
              }}
            >
              {n.title}
            </div>
            <div style={{ fontSize: 13, color: "#525252", marginBottom: 4 }}>
              {n.body}
            </div>
            <div style={{ fontSize: 12, color: "#8d8d8d" }}>
              {timeAgo(n.createdAt)}
            </div>
          </div>
        ))}

        {/* Sentinel for infinite scroll */}
        <div ref={sentinelRef} style={{ height: 1 }} />

        {loading && (
          <div style={{ padding: "1rem", textAlign: "center", color: "#6f6f6f", fontSize: 13 }}>
            Loading…
          </div>
        )}

        {!hasMore && filteredItems.length > 0 && (
          <div style={{ padding: "0.75rem", textAlign: "center", color: "#8d8d8d", fontSize: 12 }}>
            No more notifications
          </div>
        )}
      </div>
    </div>
  );
}